/* eslint-disable no-shadow */
import qs from 'qs'
import vars from 'li-components/lib/styles'

import { blobToArrayBuffer, getEffectiveUser } from '../util'
import config from '../config'

const zendeskApi = config.zendeskApi

const fieldIds = {
  url: 360001214994,
  upcs: 360001327514,
  userAgent: 360005074213,
}

export async function searchArticles(query, helpLabel) {
  const q = qs.stringify({ query, label_names: helpLabel, per_page: 10 })
  const url = `${zendeskApi}/v2/help_center/articles/search.json?${q}`

  const response = await fetch(url)
  const data = await response.json()

  data.results.sort((a, b) => a.position - b.position)

  return data
}

export async function submitRequest(form, user) {
  const url = `${zendeskApi}/v2/requests.json`

  const postData = {
    request: {
      subject: form.subject,
      comment: {
        body: appendContextualInfo(form.description),
        uploads: form.uploads.map(file => file.token).filter(Boolean),
      },
      fields: {
        [fieldIds.upcs]: form.upcs,
        [fieldIds.url]: location.href,
        [fieldIds.userAgent]: navigator.userAgent,
      },
      requester: {
        email: user.email,
        name: user.fullName,
      },
      ticket_form_id: form.issueType,
      priority: form.priority.toLowerCase(),
    },
  }

  const response = await fetch(url, {
    method: 'post',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(postData),
  })

  const data = await response.json()

  return {
    data,
    response,
  }

  function appendContextualInfo(description) {
    return `${description}\n\n---------\nSubmitted from ${location.href}`
  }
}

export async function uploadAttachment(file) {
  try {
    const url = `${zendeskApi}/v2/uploads.json?filename=${file.name}`
    const data = await blobToArrayBuffer(file)

    const response = await fetch(url, {
      method: 'post',
      headers: {
        'Content-Type': 'application/binary',
      },
      body: data,
    })

    return response.json()
  } catch (error) {
    file.error = 'unknown error'
    return file
  }
}

export function initZendeskWidget() {
  window.zESettings = {
    webWidget: {
      offset: {
        horizontal: '350px',
      },
      color: {
        theme: vars.brandPrimaryAccessible,
      },
      contactForm: {
        suppress: true,
      },
      helpCenter: {
        suppress: true,
      },
      launcher: {
        suppress: true,
      },
      chat: {
        suppress: true,
      },
    },
  }

  /* eslint-disable */
  window.zEmbed ||
    (function(e, t) {
      var n,
        o,
        d,
        i,
        s,
        a = [],
        r = document.createElement('iframe')
      ;(window.zEmbed = function() {
        a.push(arguments)
      }),
        (window.zE = window.zE || window.zEmbed),
        (r.src = 'javascript:false'),
        (r.title = ''),
        (r.role = 'presentation'),
        ((r.frameElement || r).style.cssText = 'display: none'),
        (d = document.getElementsByTagName('script')),
        (d = d[d.length - 1]),
        d.parentNode.insertBefore(r, d),
        (i = r.contentWindow),
        (s = i.document)
      try {
        o = s
      } catch (e) {
        ;(n = document.domain),
          (r.src =
            'javascript:var d=document.open();d.domain="' + n + '";void(0);'),
          (o = s)
      }
      ;(o.open()._l = function() {
        var e = this.createElement('script')
        n && (this.domain = n),
          (e.id = 'js-iframe-async'),
          (e.src = 'https://assets.zendesk.com/embeddable_framework/main.js'),
          (this.t = +new Date()),
          (this.zendeskHost = 'labelinsight.zendesk.com'),
          (this.zEQueue = a),
          this.body.appendChild(e)
      }),
        o.write('<body onload="document._l();">'),
        o.close()
    })()
  /* eslint-enable */

  window.zE(() => {
    const user = getEffectiveUser()

    window.zE.identify({
      name: user.fullName,
      email: user.email,
    })
  })
}

export async function getReleaseNotes(helpLabelOrAppId) {
  const results = await getPendoReleaseNotes(helpLabelOrAppId)

  return results.length
    ? filter(results.slice().sort(sortByDateDescending))
    : []

  function sortByDateDescending(a = {}, b = {}) {
    return a.date && b.date ? b.date - a.date : 0
  }

  function filter(guides) {
    return guides.reduce(
      (releaseNotes, releaseNote) =>
        isPubliclyAccessible(releaseNote) && !isExpired(releaseNote)
          ? releaseNotes.concat(releaseNote)
          : releaseNotes,
      []
    )

    function isExpired(releaseNote) {
      return (
        releaseNote.expiresAfter !== null &&
        releaseNote.expiresAfter < Date.now()
      )
    }

    function isPubliclyAccessible(releaseNote) {
      return (
        releaseNote.state === 'public' || process.env.NODE_ENV !== 'production'
      )
    }
  }
}

export async function getPendoPageId(helpLabelOrAppId) {
  const response = await fetch(`${config.pendoApi}/aggregation`, {
    method: 'POST',
    headers: config.pendoApiHeaders,
    body: JSON.stringify({
      response: {
        mimeType: 'application/json',
      },
      request: {
        name: 'page-id',
        pipeline: [
          {
            source: {
              pages: null,
            },
          },
          {
            filter: `name=="${helpLabelOrAppId}"`,
          },
          {
            select: {
              id: 'id',
            },
          },
        ],
      },
    }),
  })

  return (((await response.json()).results || [])[0] || {}).id
}

export async function getPendoReleaseNotes(helpLabelOrAppId) {
  const pageId = await getPendoPageId(helpLabelOrAppId)

  if (pageId) {
    const response = await fetch(`${config.pendoApi}/aggregation`, {
      method: 'POST',
      headers: config.pendoApiHeaders,
      body: JSON.stringify({
        response: {
          mimeType: 'application/json',
        },
        request: {
          name: 'guides-by-page-id',
          pipeline: [
            {
              source: {
                guides: null,
              },
            },
            {
              filter:
                'state != "draft" &&' +
                'attributes.type == "whatsnew" &&' +
                `audienceUiHint.filters[0].pageId == "${pageId}"`,
            },
            {
              select: {
                expiresAfter: 'expiresAfter',
                state: 'state',
                id: 'steps[0].id',
                date: 'steps[0].lastUpdatedAt',
                title: 'steps[0].attributes.variables.header',
                content: 'steps[0].attributes.variables.content',
                description: 'steps[0].attributes.variables.description',
                image: 'steps[0].attributes.variables.image',
              },
            },
          ],
        },
      }),
    })

    return (await response.json()).results || []
  }

  return []
}



// WEBPACK FOOTER //
// ./~/label-insight-app-shell/src/Application/help/api.js